import { useCallback, useMemo, useState } from 'react';
import {
  AlertCircle,
  Ban,
  Bell,
  BellOff,
  Check,
  ExternalLink,
  Eye,
  Loader2,
  MailX,
  Search,
  Tag,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getInitials } from '@/features/email-list/utils/emailFormatting';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { getErrorMessage } from '@/lib/error';
import { safePushState } from '@/lib/iosPwaNavigation';
import { buildMailboxHashPath } from '@/lib/urlRouting';
import { unsubscribeFromSender } from '@/api/subscriptions';
import { useSenderProfileStore } from '@/stores/senderProfileStore';
import { useContactStore } from '@/stores/contactStore';
import { useUIStore } from '@/stores/uiStore';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import type { SenderProfile } from '@/api/senderProfile';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * 12-week volume sparkline. Single series → one theme hue, no legend; the
 * numbers live in the tooltip (native title) and the summary text beside it.
 */
function VolumeSparkline({ histogram }: { histogram: SenderProfile['histogram'] }) {
  const max = Math.max(1, ...histogram.map((entry) => entry.count));
  if (!histogram.length) {
    return <p className="text-xs text-muted-foreground">No mail in the last 12 weeks</p>;
  }
  return (
    <div className="flex h-12 items-end gap-0.5" role="img" aria-label="Messages per week, last 12 weeks">
      {histogram.map((entry) => (
        <div
          key={entry.week}
          title={`Week of ${formatDate(entry.week)}: ${entry.count} message${entry.count === 1 ? '' : 's'}`}
          className="flex-1 rounded-t-[3px] bg-primary/60 transition-colors hover:bg-primary"
          style={{ height: `${Math.max(6, Math.round((entry.count / max) * 100))}%` }}
        />
      ))}
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export function SenderProfileSheet() {
  const open = useSenderProfileStore((state) => state.open);
  const email = useSenderProfileStore((state) => state.email);
  const profile = useSenderProfileStore((state) => state.profile);
  const loading = useSenderProfileStore((state) => state.loading);
  const error = useSenderProfileStore((state) => state.error);
  const close = useSenderProfileStore((state) => state.close);
  const refresh = useSenderProfileStore((state) => state.refresh);

  const approveContact = useContactStore((state) => state.approveContact);
  const blockContact = useContactStore((state) => state.blockContact);
  const setContactNotifications = useContactStore((state) => state.setContactNotifications);

  const [actionBusy, setActionBusy] = useState(false);

  const approvalSummary = useMemo(() => {
    if (!profile?.contacts.length) return null;
    const states = new Set(profile.contacts.map((contact) => contact.approvedState));
    if (states.has('blocked') && states.size === 1) return 'blocked';
    if (states.has('approved') && states.size === 1) return 'approved';
    if (states.has('pending')) return 'pending';
    return 'mixed';
  }, [profile]);

  const notificationsEnabled = useMemo(
    () => Boolean(profile?.contacts.some((contact) => contact.notificationsEnabled)),
    [profile]
  );

  const unsubscribedAt = useMemo(
    () => profile?.contacts.map((contact) => contact.unsubscribedAt).find(Boolean) ?? null,
    [profile]
  );

  const runContactAction = useCallback(
    async (action: (contactId: string, options: { accountId: string }) => Promise<unknown>) => {
      if (!profile) return;
      setActionBusy(true);
      try {
        await Promise.all(
          profile.contacts.map((contact) =>
            action(contact.id, { accountId: contact.accountId })
          )
        );
        await refresh();
      } catch (err) {
        toast.error(getErrorMessage(err) ?? 'Action failed');
      } finally {
        setActionBusy(false);
      }
    },
    [profile, refresh]
  );

  const handleApprove = useCallback(
    () => runContactAction((id, options) => approveContact(id, options)),
    [approveContact, runContactAction]
  );
  const handleBlock = useCallback(
    () => runContactAction((id, options) => blockContact(id, options)),
    [blockContact, runContactAction]
  );

  const handleToggleNotifications = useCallback(
    async (enabled: boolean) => {
      if (!profile) return;
      setActionBusy(true);
      try {
        await Promise.all(
          profile.contacts.map((contact) =>
            setContactNotifications(contact.id, enabled, { accountId: contact.accountId })
          )
        );
        await refresh();
      } catch (err) {
        toast.error(getErrorMessage(err) ?? 'Failed to update notifications');
      } finally {
        setActionBusy(false);
      }
    },
    [profile, refresh, setContactNotifications]
  );

  const handleUnsubscribe = useCallback(async () => {
    if (!email) return;
    // Link-only: open synchronously inside the click's user-activation window
    // (awaiting first gets the popup blocked) and only record after it opened.
    const unsubscribe = profile?.unsubscribe ?? null;
    const linkOnly =
      Boolean(unsubscribe?.url)
      && !unsubscribe!.oneClick
      && !unsubscribe!.url!.startsWith('mailto:');
    if (linkOnly) {
      const opened = window.open(unsubscribe!.url!, '_blank', 'noopener,noreferrer');
      if (!opened) {
        toast.error('Popup blocked — allow popups to open the unsubscribe page');
        return;
      }
    }
    setActionBusy(true);
    try {
      const result = await unsubscribeFromSender(UNIFIED_ACCOUNT_ID, email);
      if (result.method === 'link') {
        if (!linkOnly) {
          window.open(result.url, '_blank', 'noopener,noreferrer');
        }
      } else if (result.method === 'mailto') {
        toast.success(`Unsubscribe email sent to ${result.to}`);
      } else {
        toast.success('Unsubscribed');
      }
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err) ?? 'Failed to unsubscribe');
    } finally {
      setActionBusy(false);
    }
  }, [email, profile, refresh]);

  const handleOpenMessage = useCallback(
    (messageId: string) => {
      close();
      safePushState(
        buildMailboxHashPath(UNIFIED_ACCOUNT_ID, `${UNIFIED_ACCOUNT_ID}:inbox`, messageId, null)
      );
      useUIStore.getState().setMailView('mailbox');
      useUIStore.getState().setActiveWorkspace('mail');
    },
    [close]
  );

  const handleSearchAll = useCallback(() => {
    if (!email) return;
    close();
    safePushState(`#/?q=${encodeURIComponent(`from:${email}`)}`);
    useUIStore.getState().setMailView('mailbox');
    useUIStore.getState().setActiveWorkspace('mail');
  }, [close, email]);

  const logoUrl = email ? getBrandfetchLogoUrl(email) : null;

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="pb-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 flex-shrink-0 bg-muted">
              {logoUrl && <AvatarImage src={logoUrl} alt="" className="object-contain" />}
              <AvatarFallback>
                {getInitials(profile?.name ?? undefined, email ?? undefined)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <SheetTitle className="truncate text-base">
                {profile?.name || email || 'Sender'}
              </SheetTitle>
              <SheetDescription className="truncate">{email}</SheetDescription>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {approvalSummary === 'blocked' && (
              <Badge variant="outline" className="text-[10px] text-danger-ink">Blocked</Badge>
            )}
            {approvalSummary === 'approved' && (
              <Badge variant="outline" className="text-[10px] text-success-ink">Approved</Badge>
            )}
            {approvalSummary === 'pending' && (
              <Badge variant="outline" className="text-[10px] text-warning-ink">Pending</Badge>
            )}
            {unsubscribedAt && (
              <Badge variant="outline" className="text-[10px] text-success-ink">
                Unsubscribed {formatDate(unsubscribedAt)}
              </Badge>
            )}
            {profile?.labels.map((label) => (
              <Badge key={label.id} variant="outline" className="gap-1 text-[10px]">
                <Tag className="h-2.5 w-2.5" style={label.color ? { color: label.color } : undefined} />
                {label.name}
              </Badge>
            ))}
          </div>
        </SheetHeader>

        {loading && !profile && (
          <div className="flex flex-1 items-center justify-center p-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="mx-4 flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {profile && (
          <div className="flex flex-col gap-4 px-4 pb-6">
            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              {approvalSummary !== 'approved' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 px-2.5 text-xs"
                  disabled={actionBusy || !profile.contacts.length}
                  onClick={handleApprove}
                >
                  <Check className="h-3 w-3" /> Approve
                </Button>
              )}
              {approvalSummary !== 'blocked' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 px-2.5 text-xs text-danger-ink"
                  disabled={actionBusy || !profile.contacts.length}
                  onClick={handleBlock}
                >
                  <Ban className="h-3 w-3" /> Block
                </Button>
              )}
              {profile.unsubscribe && !unsubscribedAt && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 px-2.5 text-xs"
                  disabled={actionBusy}
                  onClick={handleUnsubscribe}
                >
                  {profile.unsubscribe.oneClick ? (
                    <Zap className="h-3 w-3 text-warning-ink" />
                  ) : (
                    <MailX className="h-3 w-3" />
                  )}
                  Unsubscribe
                </Button>
              )}
              {profile.contacts.length > 0 && (
                <div className="ml-auto flex items-center gap-1.5">
                  {notificationsEnabled ? (
                    <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <BellOff className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <Switch
                    checked={notificationsEnabled}
                    disabled={actionBusy}
                    onCheckedChange={handleToggleNotifications}
                    aria-label="Notifications for this sender"
                  />
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-2 rounded-lg border border-border/50 p-3">
              <StatCell label="Total" value={String(profile.stats.total)} />
              <StatCell label="Last 30d" value={String(profile.stats.last30d)} />
              <StatCell label="Unread" value={String(profile.stats.unread)} />
              <StatCell label="Since" value={formatDate(profile.stats.firstMessageAt)} />
            </div>

            {/* Volume sparkline */}
            <div>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Volume · 12 weeks
              </div>
              <VolumeSparkline histogram={profile.histogram} />
            </div>

            {/* Open tracking (mail we sent them) */}
            {profile.tracking.trackedSends > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Eye className="h-3.5 w-3.5" />
                Opened {profile.tracking.openedSends} of {profile.tracking.trackedSends} tracked
                emails you sent
                {profile.tracking.lastOpenedAt
                  ? ` · last ${formatDate(profile.tracking.lastOpenedAt)}`
                  : ''}
              </div>
            )}

            {/* Recent messages */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Recent messages
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
                  onClick={handleSearchAll}
                >
                  <Search className="h-3 w-3" /> View all
                </Button>
              </div>
              <div className="space-y-0.5">
                {profile.recentMessages.map((message) => (
                  <button
                    key={message.id}
                    type="button"
                    onClick={() => handleOpenMessage(message.id)}
                    className="flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted/60"
                  >
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs',
                        message.seen ? 'text-muted-foreground' : 'font-medium text-foreground'
                      )}
                    >
                      {message.subject || '(no subject)'}
                    </span>
                    <span className="flex-shrink-0 text-[10px] text-muted-foreground">
                      {formatDate(message.date)}
                    </span>
                  </button>
                ))}
                {!profile.recentMessages.length && (
                  <p className="px-2 text-xs text-muted-foreground">No messages</p>
                )}
              </div>
            </div>

            {profile.unsubscribe?.url && (
              <a
                href={profile.unsubscribe.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" /> Sender's unsubscribe page
              </a>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
